import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForBindingId } from '../../../forBinding';
import api from '../../http';
import { toApiError } from '../utils';
import type { FertilizerPack } from './useGetFertilizerPacks';

export type FertilizerPackPayload = Omit<FertilizerPack, '_id' | 'userId'>;

export function usePostFertilizerPack() {
	const queryClient = useQueryClient();
	const forBinding = useForBindingId();

	return useMutation<FertilizerPack, Error, FertilizerPackPayload>({
		mutationFn: async (payload) => {
			try {
				const { data } = await api.post<FertilizerPack>(
					'/advices/fertilizer-pack/create',
					payload,
					{ params: { forBinding } }
				);
				return data;
			} catch (error) {
				throw toApiError(error, 'Erro ao criar o pacote de produtos');
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['fertilizer-packs'] });
		},
	});
}

export function useUpdateFertilizerPack() {
	const queryClient = useQueryClient();
	const forBinding = useForBindingId();

	return useMutation<FertilizerPack, Error, { id: string; payload: FertilizerPackPayload }>({
		mutationFn: async ({ id, payload }) => {
			try {
				const { data } = await api.put<FertilizerPack>(
					`/advices/fertilizer-pack/update/${id}`,
					payload,
					{ params: { forBinding } }
				);
				if (!data?._id) {
					throw new Error('Erro ao atualizar o pacote de produtos');
				}
				return data;
			} catch (error) {
				throw toApiError(error, 'Erro ao atualizar o pacote de produtos');
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['fertilizer-packs'] });
		},
	});
}

export function useDeleteFertilizerPack() {
	const queryClient = useQueryClient();
	const forBinding = useForBindingId();

	return useMutation<void, Error, string>({
		mutationFn: async (id) => {
			try {
				await api.delete(`/advices/fertilizer-pack/delete/${id}`, {
					params: { forBinding },
				});
			} catch (error) {
				throw toApiError(error, 'Erro ao excluir o pacote de produtos');
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['fertilizer-packs'] });
		},
	});
}

export function useDuplicateFertilizerPack() {
	const queryClient = useQueryClient();
	const forBinding = useForBindingId();

	return useMutation<FertilizerPack, Error, string>({
		mutationFn: async (id) => {
			try {
				const { data } = await api.post<FertilizerPack>(
					`/advices/fertilizer-pack/duplicate/${id}/1`,
					{},
					{ params: { forBinding } }
				);
				return data;
			} catch (error) {
				throw toApiError(error, 'Erro ao duplicar o pacote de produtos');
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['fertilizer-packs'] });
		},
	});
}
